import * as Dialog from '@radix-ui/react-dialog'
import type { ReactNode } from 'react'
import { useTranslation } from '../localization/useTranslation'
import { usePortalContainer } from '../providers/portal'
import { IconX } from '../ui/icons'
import { OverlayReporter } from './OverlayReporter'

/** A single drop-off point (subset of Core's `Dropoff` used on the Confirm step). */
export interface DropoffPoint {
  name?: string | null
  address?: string | null
  address2?: string | null
  zipcode?: string | null
  city?: string | null
  opening_hours?: Array<string | null> | null
  qr_code?: boolean | null
}

interface DropoffDialogProps {
  trigger: ReactNode
  dropOffs: DropoffPoint[]
  /** The label is QR-based, so show each point's QR support (ported from v1's Dropoff dialog). */
  showQr: boolean
}

/**
 * "Find your nearest drop-off point" — a modal list of the carrier's nearby points for this
 * trade-in (from `tradeIn.dropOffPoints`). Ported from v1's Dropoff dialog: name + address, and,
 * for QR-based labels, whether each point supports QR drop-off. Radix Dialog portalled into the
 * shadow-root container like the other dialogs.
 */
export function DropoffDialog({ trigger, dropOffs, showQr }: DropoffDialogProps) {
  const container = usePortalContainer()
  const { t } = useTranslation()

  return (
    // modal={false} so Radix's scroll-lock doesn't block the mouse wheel in our shadow root (see TermsDialog).
    <Dialog.Root modal={false}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal container={container ?? undefined}>
        {/* Plain scrim, not Dialog.Overlay: Radix renders Overlay as null when modal={false}. As a
            direct Portal child it's still open-state gated, and clicking it closes the dialog. */}
        <div className="modal-scrim" aria-hidden />
        <Dialog.Content className="modal dropoff-modal" aria-describedby={undefined}>
          <OverlayReporter />
          <div className="modal-head">
            <Dialog.Title asChild>
              <h2>{t('dropoff.title')}</h2>
            </Dialog.Title>
            <Dialog.Close className="icon-btn" aria-label={t('terms.close')}>
              <IconX />
            </Dialog.Close>
          </div>
          <div className="modal-body dropoff-list">
            {dropOffs.map((d, i) => {
              const street = [d.address, d.address2].filter(Boolean).join(', ')
              const area = [d.zipcode, d.city].filter(Boolean).join(' ')
              const hours = (d.opening_hours ?? []).filter(Boolean) as string[]
              return (
                <div className="dropoff" key={`${d.name ?? 'point'}-${i}`}>
                  <div className="dropoff-info">
                    <div className="dropoff-name">{d.name}</div>
                    {street && <div className="dropoff-addr">{street}</div>}
                    {area && <div className="dropoff-addr">{area}</div>}
                    {hours.length > 0 && <div className="dropoff-hours">{hours.join(' · ')}</div>}
                  </div>
                  {showQr && (
                    <span
                      className={`dropoff-qr ${d.qr_code ? 'is-on' : 'is-off'}`}
                      title={d.qr_code ? t('dropoff.qrSupported') : t('dropoff.qrNotSupported')}
                    >
                      QR
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
